import React from 'react';

import { ReportContext } from './hooks/use-context';
import type { EventReportData, QuestionData } from './types';

// Helper function to format date from API response
function formatDate(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  return datePart || '';
}

// Helper function to format time from API response
function formatTime(dateString: string): string {
  if (!dateString) return '';
  const parts = dateString.split(' ');
  if (parts.length < 2) return '';
  const [hours, minutes] = parts[1].split(':');
  return `${hours}:${minutes}`;
}

// Helper to extract month name (pt-BR) from date string like 'DD/MM/YYYY HH:mm'
function getMonthNameFromDateString(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  const parts = (datePart || '').split('/');
  if (parts.length < 2) return '';
  const monthNum = Number(parts[1]);
  const MONTHS = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return '';
  return MONTHS[monthNum - 1];
}

// Helper function to get answer content by question NAME
function getAnswerContentByName(
  questions: QuestionData[],
  questionName: string,
): string {
  const question = questions.find((q) => q.name === questionName);
  if (!question || !question.answers || question.answers.length === 0)
    return '';

  const answer = question.answers[0];
  return answer.option || answer.content || '';
}

export function IzuReportOilControl(props: { reportData: EventReportData }) {
  const { reportData } = props;

  // Extract questions from the first section
  const questions =
    reportData?.body?.items?.[0]?.sections?.[0]?.questions || [];
  const header = reportData?.header;
  const firstItem = reportData?.body?.items?.[0];

  // Question NAMES based on form definition
  const QUESTION_NAMES = {
    filtragem: 'Filtragem Realizada',
    troca: 'Troca Realizada',
    responsavel: 'Nome do Colaborador Responsável',
    assinatura: 'Assinatura do Colaborador Responsável',
  } as const;

  const eventDateTime =
    header?.event_finished_at ||
    header?.event_date_time ||
    header?.event_created_at ||
    '';
  const date = formatDate(eventDateTime);
  const time = formatTime(eventDateTime);
  const monthName = getMonthNameFromDateString(eventDateTime);
  const filtragem = getAnswerContentByName(questions, QUESTION_NAMES.filtragem);
  const troca = getAnswerContentByName(questions, QUESTION_NAMES.troca);
  const responsavel = getAnswerContentByName(
    questions,
    QUESTION_NAMES.responsavel,
  );
  // Signature is available by name if needed elsewhere:
  // const assinatura = getAnswerContentByName(questions, QUESTION_NAMES.assinatura);

  // Footer data from API
  const elaboradoPor = firstItem?.formulary_last_author || '-';
  const dataElaboracao = firstItem?.formulary_updated_at || '-';
  const aprovadoPor = header?.event_validated_author || '-';
  const dataRevisao = header?.event_validated_at || '-';

  return (
    <ReportContext value={reportData}>
      <div
        style={{
          width: '210mm',
          minHeight: '297mm',
          backgroundColor: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          padding: '20mm',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Header Section */}
        <div style={{ marginBottom: '20px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '2px solid #000',
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    width: '20%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                  }}
                >
                  {header?.logo && (
                    <img
                      src={header.logo}
                      alt="Logo"
                      style={{ maxWidth: '100px', height: 'auto' }}
                    />
                  )}
                </td>
                <td
                  style={{
                    width: '50%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  {reportData.header?.event_name}
                </td>
                <td
                  style={{
                    width: '30%',
                    padding: '10px',
                    border: '1px solid #000',
                  }}
                >
                  <div style={{ marginBottom: '5px' }}>PGQ4-00</div>
                  <div>Página: 1 de 1</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Month and Area Section */}
        <div style={{ marginBottom: '10px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>Mês: {monthName}</div>
            {header?.local?.name && <div>Área: {header?.local?.name}</div>}
          </div>
        </div>

        {/* Main Data Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            marginBottom: '20px',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#808080', color: '#fff' }}>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Data
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Hora
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Filtragem
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Troca
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Responsável
              </th>
            </tr>
          </thead>
          <tbody>
            {/* First row with actual data */}
            <tr>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  textAlign: 'center',
                }}
              >
                {date}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  textAlign: 'center',
                }}
              >
                {time}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  textAlign: 'center',
                }}
              >
                {filtragem}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  textAlign: 'center',
                }}
              >
                {troca}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  textAlign: 'center',
                }}
              >
                {responsavel}
              </td>
            </tr>
            {/* Empty rows for additional entries */}
            {Array.from({ length: 8 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`empty_${index}`}>
                <td
                  style={{
                    border: '1px solid #000',
                    padding: '8px',
                    height: '30px',
                  }}
                >
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '8px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '8px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '8px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '8px' }}>
                  &nbsp;
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Observations Section */}
        <div style={{ marginBottom: '20px' }}>
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '5px',
              fontSize: '12px',
            }}
          >
            Orientações:
          </div>
          <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
            Os óleos e gorduras utilizados <strong>não</strong> devem ser
            aquecidos a temperaturas <strong>superiores a 180°C</strong> (cento
            e oitenta graus Celsius), sendo substituídos{' '}
            <strong>imediatamente</strong> sempre que houver alteração evidente
            das características físico-químicas ou sensoriais, tais como aroma e
            sabor, e formação intensa de espuma e fumaça.
            <div style={{ marginTop: '10px' }}>
              <span style={{ marginRight: '40px' }}>
                <strong>Frequência de filtragem:</strong> diária
              </span>
              <span>
                <strong>Frequência de troca:</strong> 3 dias
              </span>
            </div>
          </div>
        </div>

        {/* Footer Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            fontSize: '11px',
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Elaborado por:</strong> {elaboradoPor}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Aprovado por:</strong> {aprovadoPor}
              </td>
              {/* <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '34%',
                }}
              >
                <strong>Revisão:</strong> 003
              </td> */}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '8px' }}>
                <strong>Data de elaboração:</strong> {dataElaboracao}
              </td>
              <td style={{ border: '1px solid #000', padding: '8px' }}>
                <strong>Data de aplicação:</strong> {date} {time}
              </td>
              <td style={{ border: '1px solid #000', padding: '8px' }}>
                <strong>Data de aprovação/revisão:</strong> {dataRevisao}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ReportContext>
  );
}
